/**
 * Ask this meeting (spec 06 card 6, §2.5 row 6): a plain-language question → a bulleted answer built
 * only from this meeting's transcript (POST /api/dashboard/meetings/<id>/ask; on another city's site, which has no
 * accounts, POST /api/meetings/<id>/ask for anyone: worker/src/city-meetings.js). Every point shows the
 * speakers' faces and a timestamp citation that seeks the video; a point without a citation is never
 * shown. Anything can focus this bar with the window event "ask:focus".
 */
import { useEffect, useRef, useState } from "react";
import { ArrowUp, Info, LoaderCircle, Play, Sparkles } from "lucide-react";
import { useMeeting } from "../../stores/meetingStore";
import { toastStore } from "../../stores/toastStore";
import { FaceStack } from "../shared/Face";
import { seekTo } from "./vimeoClock";
import { clock } from "../../lib/format";
import { prefersReducedMotion } from "../../motion";
import type { AskAnswer } from "../../types";
import { getViewer, signInToAct } from "../../app/viewer";
import { site } from "../../app/site";

/** Points the UI may show: each needs at least one citation with a time. */
export const citedPoints = (a: AskAnswer) =>
  a.points.map((p) => ({ ...p, citations: p.citations.filter((c) => Number.isFinite(c.at)) })).filter((p) => p.text && p.citations.length > 0);

export function AskMeeting() {
  const meetingId = useMeeting((s) => s.meeting?.id ?? null);
  const people = useMeeting((s) => s.people);
  const [question, setQuestion] = useState("");
  const [pending, setPending] = useState(false);
  const [answer, setAnswer] = useState<AskAnswer | null>(null);
  const input = useRef<HTMLInputElement>(null);
  const form = useRef<HTMLFormElement>(null);

  useEffect(() => {
    const focus = () => requestAnimationFrame(() => {
      form.current?.scrollIntoView({ behavior: prefersReducedMotion() ? "auto" : "smooth", block: "center" });
      input.current?.focus({ preventScroll: true });
    });
    addEventListener("ask:focus", focus);
    return () => removeEventListener("ask:focus", focus);
  }, []);
  // A link to the meeting's #ask (the home page's "Ask this meeting") opens the page at the question bar.
  useEffect(() => {
    if (meetingId && location.hash === "#ask") dispatchEvent(new Event("ask:focus"));
  }, [meetingId]);

  const ask = async (e: React.FormEvent) => {
    e.preventDefault();
    const q = question.trim();
    if (!q || pending || !meetingId) return;
    if (!site.city && !getViewer().admitted) return signInToAct(); // Chicago's public meeting page: Ask needs dashboard access
    setPending(true);
    const res = await fetch(site.city ? `/api/meetings/${encodeURIComponent(meetingId)}/ask` : `/api/dashboard/meetings/${encodeURIComponent(meetingId)}/ask`, {
      method: "POST", credentials: "include", headers: { "content-type": "application/json" }, body: JSON.stringify({ question: q }),
    }).catch(() => null);
    const body = res ? ((await res.json().catch(() => null)) as (AskAnswer & { error?: string }) | null) : null;
    setPending(false);
    // Our own messages for the asker's request (too short, too many this hour); anything else is a plain "try again".
    if (!res?.ok || !body?.points) { toastStore.getState().show({ message: res && res.status < 500 && body?.error ? body.error : "Couldn't answer that. Try again." }); return; }
    setAnswer(body);
  };

  const points = answer ? citedPoints(answer) : [];
  return (
    <div className="ask-meeting">
      <form ref={form} className="ask-bar" onSubmit={(e) => void ask(e)}>
        <Sparkles size={15} />
        <input ref={input} aria-label="Ask this meeting" placeholder="Ask this meeting, e.g. “What was said about minority contracting?”"
          value={question} onChange={(e) => setQuestion(e.target.value)} maxLength={300} />
        <button className="ask-send" aria-label="Ask" disabled={pending}>
          {pending ? <LoaderCircle size={13} className="ask-spin" /> : <ArrowUp size={13} />}
        </button>
      </form>
      {answer && (
        <div className="ask-answer" key={answer.question} aria-live="polite">
          {points.length > 0 ? (
            <ul className="ask-points">
              {points.map((p, i) => {
                const ids = [...new Set(p.citations.flatMap((c) => c.personIds))];
                const faces = ids.map((id) => people[id]).filter(Boolean);
                return (
                  <li key={i} className="ask-point">
                    <span className="ask-faces">{faces.length > 0 && <FaceStack people={faces} size={18} max={3} />}</span>
                    <span className="ask-body">
                      <span className="ask-text">{p.text}</span>
                      <span className="ask-cites">
                        {p.citations.map((c) => (
                          <button key={c.at} className="ask-cite" onClick={() => seekTo(c.at)}>
                            <Play size={10} />{clock(c.at)}
                          </button>
                        ))}
                      </span>
                    </span>
                  </li>
                );
              })}
            </ul>
          ) : (
            <p className="ask-none">Not discussed in this meeting.</p>
          )}
          <p className="ask-foot"><Info size={12} />From this meeting's transcript only. Check the sources.</p>
        </div>
      )}
    </div>
  );
}
